// Adopted from: https://github.com/pshihn/bezier-points/blob/master/lib/index.ts
import * as V from '@folkjs/geometry/Vector2';
export const MAX_Z_INDEX = 2147483647;
// Distance squared from a point p to the line segment vw
function distanceToSegmentSq(p, v, w) {
    const l2 = V.distanceSquared(v, w);
    if (l2 === 0) {
        return V.distanceSquared(p, v);
    }
    let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
    t = Math.max(0, Math.min(1, t));
    return V.distanceSquared(p, V.lerp(v, w, t));
}
export function lerp(a, b, alpha) {
    return a + (b - a) * alpha;
}
// Adapted from https://seant23.wordpress.com/2010/11/12/offset-bezier-curves/
function flatness(points, offset) {
    const p1 = points[offset + 0];
    const p2 = points[offset + 1];
    const p3 = points[offset + 2];
    const p4 = points[offset + 3];
    let ux = 3 * p2.x - 2 * p1.x - p4.x;
    ux *= ux;
    let uy = 3 * p2.y - 2 * p1.y - p4.y;
    uy *= uy;
    let vx = 3 * p3.x - 2 * p4.x - p1.x;
    vx *= vx;
    let vy = 3 * p3.y - 2 * p4.y - p1.y;
    vy *= vy;
    if (ux < vx) {
        ux = vx;
    }
    if (uy < vy) {
        uy = vy;
    }
    return ux + uy;
}
function getPointsOnBezierCurveWithSplitting(points, offset, tolerance, newPoints) {
    const outPoints = newPoints || [];
    if (flatness(points, offset) < tolerance) {
        const p0 = points[offset + 0];
        if (outPoints.length) {
            const d = V.distance(outPoints[outPoints.length - 1], p0);
            if (d > 1) {
                outPoints.push(p0);
            }
        }
        else {
            outPoints.push(p0);
        }
        outPoints.push(points[offset + 3]);
    }
    else {
        // subdivide
        const t = 0.5;
        const p1 = points[offset + 0];
        const p2 = points[offset + 1];
        const p3 = points[offset + 2];
        const p4 = points[offset + 3];
        const q1 = V.lerp(p1, p2, t);
        const q2 = V.lerp(p2, p3, t);
        const q3 = V.lerp(p3, p4, t);
        const r1 = V.lerp(q1, q2, t);
        const r2 = V.lerp(q2, q3, t);
        const red = V.lerp(r1, r2, t);
        getPointsOnBezierCurveWithSplitting([p1, q1, r1, red], 0, tolerance, outPoints);
        getPointsOnBezierCurveWithSplitting([red, r2, q3, p4], 0, tolerance, outPoints);
    }
    return outPoints;
}
export function simplify(points, distance) {
    return simplifyPoints(points, 0, points.length, distance);
}
// Ramer–Douglas–Peucker algorithm
// https://en.wikipedia.org/wiki/Ramer%E2%80%93Douglas%E2%80%93Peucker_algorithm
export function simplifyPoints(points, start, end, epsilon, newPoints) {
    const outPoints = newPoints || [];
    // find the most distance point from the endpoints
    const s = points[start];
    const e = points[end - 1];
    let maxDistSq = 0;
    let maxNdx = 1;
    for (let i = start + 1; i < end - 1; ++i) {
        const distSq = distanceToSegmentSq(points[i], s, e);
        if (distSq > maxDistSq) {
            maxDistSq = distSq;
            maxNdx = i;
        }
    }
    // if that point is too far, split
    if (Math.sqrt(maxDistSq) > epsilon) {
        simplifyPoints(points, start, maxNdx + 1, epsilon, outPoints);
        simplifyPoints(points, maxNdx, end, epsilon, outPoints);
    }
    else {
        if (!outPoints.length) {
            outPoints.push(s);
        }
        outPoints.push(e);
    }
    return outPoints;
}
export function pointsOnBezierCurves(points, tolerance = 0.15, distance) {
    const newPoints = [];
    const numSegments = (points.length - 1) / 3;
    for (let i = 0; i < numSegments; i++) {
        const offset = i * 3;
        getPointsOnBezierCurveWithSplitting(points, offset, tolerance, newPoints);
    }
    if (distance && distance > 0) {
        return simplifyPoints(newPoints, 0, newPoints.length, distance);
    }
    return newPoints;
}
export function getSvgPathFromStroke(stroke) {
    if (stroke.length === 0)
        return '';
    for (const point of stroke) {
        point.x = Math.round(point.x * 100) / 100;
        point.y = Math.round(point.y * 100) / 100;
    }
    const d = stroke.reduce((acc, { x: x0, y: y0 }, i, arr) => {
        const { x: x1, y: y1 } = arr[(i + 1) % arr.length];
        acc.push(x0, y0, (x0 + x1) / 2, (y0 + y1) / 2);
        return acc;
    }, ['M', stroke[0].x, stroke[0].y, 'Q']);
    d.push('Z');
    return d.join(' ');
}
export function verticesToPolygon(vertices) {
    if (vertices.length === 0)
        return '';
    return `polygon(${vertices.map((vertex) => `${vertex.x}px ${vertex.y}px`).join(', ')})`;
}
const vertexRegex = /(?<x>-?([0-9]*[.])?[0-9]+),\s*(?<y>-?([0-9]*[.])?[0-9]+)/;
export function parseVertex(str) {
    const results = vertexRegex.exec(str);
    if (results === null)
        return null;
    return {
        x: Number(results.groups?.x),
        y: Number(results.groups?.y),
    };
}
//# sourceMappingURL=utils.js.map